// indigo 002FAA

export interface Color {
  name: string
  hex: string
}
export const colors = {
  black: {
    name: 'Black',
    hex: '#000000',
  },
  gray: {
    name: 'Gray',
    hex: '#808080',
  },
  red: {
    name: 'Red',
    hex: '#C53030',
  },
  orange: {
    name: 'Orange',
    hex: '#ff8000',
  },
  green: {
    name: 'Green',
    hex: '#43AD8E',
  },
  neongreen: {
    name: 'Neon Green',
    hex: '#39ff14',
  },
  cyan: {
    name: 'Cyan',
    hex: '#00ffee',
  },
  blue: {
    name: 'Blue',
    hex: '#0099ff',
  },
  indigo: {
    name: 'Indigo',
    hex: '#002FAA',
  },
  purple: {
    name: 'Purple',
    hex: '#8a2be2',
  },
  pink: {
    name: 'Pink',
    hex: '#febcad',
  },
  yellow: {
    name: 'Yellow',
    hex: '#fffb00',
  },
  lime: {
    name: 'Lime',
    hex: '#00FF00',
  },
  maroon: {
    name: 'Maroon',
    hex: '#800000',
  },
  navy: {
    name: 'Navy',
    hex: '#000080',
  },
  olive: {
    name: 'Olive',
    hex: '#808000',
  },
  teal: {
    name: 'Teal',
    hex: '#008080',
  },
  silver: {
    name: 'Silver',
    hex: '#C0C0C0',
  },
  gold: {
    name: 'Gold',
    hex: '#FFD700',
  },
  skyblue: {
    name: 'Sky Blue',
    hex: '#87CEEB',
  },
  lightgreen: {
    name: 'Light Green',
    hex: '#90EE90',
  },
  scarlet: {
    name: 'Scarlet',
    hex: '#FF2400',
  },
  lightblue: {
    name: 'Light Blue',
    hex: '#ADD8E6',
  },
  hotpink: {
    name: 'Hot Pink',
    hex: '#ff69b4',
  },
  lightpurple: {
    name: 'Light Purple',
    hex: '#9370DB',
  },
} as const

export type ColorName = keyof typeof colors

// ─── Charting UI green palette ──────────────────────────────────────────────────
// Sage-green system for the encounter charting screen — EHR Spec Addendum Section 0.6.1
// ("Charting UI Visual Design System"). Companion reference: Hamilton_EHR_Charting_UI_Mockup.html,
// whose hex values are authoritative. `usage` documents where each green is applied.

export const greens: Record<string, Color> = {
  // Dark forest — the Authenticate button (primary action). Always the darkest, most
  // dominant element in the timer bar; no other button uses this color.
  forestGreen: {
    name: 'Forest Green',
    hex: '#1f4d2e',
    // usage: 'Authenticate button background (primary action).',
  },
  authenticateHover: {
    name: 'Dark Forest Green (hover)',
    hex: '#163d23',
    // usage: 'Authenticate button hover state.',
  },
  // Medium sage — the workhorse accent color.
  sage: {
    name: 'Sage',
    hex: '#4a7c59',
  },
  // Slightly darker sage — reserved for the timer strip so it reads above the sage bar.
  darkSage: {
    name: 'Dark Sage',
    hex: '#3a6b47',
  },
  // Light sage fill for selected/active states.
  mint: {
    name: 'Mint',
    hex: '#eaf3de',
  },
  // Very light sage — the sticky timer/action bar surface.
  lightSage: {
    name: 'Light Sage',
    hex: '#e8f3ec',
  },
  // Muted sage border under the timer bar.
  darkMint: {
    name: 'Dark Mint',
    hex: '#c8ddd0',
  },
  // Brightened sage for accents on dark surfaces (dark-mode active tab, unread dot, sender names).
  brightSage: {
    name: 'Bright Sage',
    hex: '#8fbf9b',
  },
} as const

export type GreenName = keyof typeof greens

// ─── Teal counterpart palette ──────────────────────────────────────────────────
// Teal equivalents of `greens`, keyed by the green they correspond to so palettes
// can be swapped programmatically (e.g. `(useTeal ? teals : greens).sage`).
// Anchored on colors.green (#43AD8E): `sage` is the reference color itself and the
// other entries keep the greens' role/lightness ladder at the same hue (~162°).
// Used by the mobile messaging redesign.

export const teals: Record<GreenName, Color> = {
  forestGreen: {
    name: 'Forest Teal',
    hex: '#1f5142',
  },
  authenticateHover: {
    name: 'Forest Teal (hover)',
    hex: '#1a4437',
  },
  sage: {
    name: 'Teal Sage',
    hex: '#43ad8e',
  },
  darkSage: {
    name: 'Dark Teal Sage',
    hex: '#2e7d66',
  },
  mint: {
    name: 'Teal Mint',
    hex: '#dbf5ed',
  },
  lightSage: {
    name: 'Light Teal Sage',
    hex: '#e5f5f0',
  },
  darkMint: {
    name: 'Dark Teal Mint',
    hex: '#b6ddd1',
  },
  brightSage: {
    name: 'Bright Teal Sage',
    hex: '#75c7ae',
  },
} as const

export type TealName = keyof typeof teals

// ─── Rose/pink counterpart palette ─────────────────────────────────────────────
// Rose equivalents of `teals`, keyed by the green they correspond to so palettes
// can be swapped programmatically (e.g. `(isPink ? pinks : teals).sage`).
// Anchored on #b6495f (~348°, raspberry rose): `sage` is the reference color itself
// and the other entries keep their teal's lightness/saturation with the hue rotated
// to match. Used by the mobile messaging redesign to tint the "other side" of a
// conversation (admin ↔ practice) with the same visual system as the teal palette.

export const pinks: Record<GreenName, Color> = {
  forestGreen: {
    name: 'Forest Rose',
    hex: '#511f29',
  },
  authenticateHover: {
    name: 'Forest Rose (hover)',
    hex: '#441a23',
  },
  sage: {
    name: 'Rose',
    hex: '#b6495f',
  },
  darkSage: {
    name: 'Dark Rose',
    hex: '#7d2e3e',
  },
  mint: {
    name: 'Blush',
    hex: '#f5dbe0',
  },
  lightSage: {
    name: 'Light Rose',
    hex: '#f5e5e8',
  },
  darkMint: {
    name: 'Dark Blush',
    hex: '#ddb6be',
  },
  brightSage: {
    name: 'Bright Rose',
    hex: '#c77586',
  },
  // Light accent tint at the same hue (colors.pink's lightness rotated to rose).
  bright: {
    name: 'Bright Pink',
    hex: '#feadbd',
  },
} as const

export type PinkName = keyof typeof pinks

export const patientColor = '#dbcca0'
